import { useState } from "react";
import { Calendar, ChevronDown, Clock, Timer } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Popover, PopoverAnchor, PopoverContent } from "@workspace/ui/components/ui/popover";
import type { AvailabilityData } from "@/lib/api";
import { FULL_DAY_HOURS, quarterHourStartOptions } from "@/lib/hourly-booking";
import { SingleDateCalendar } from "./SingleDateCalendar";

export interface HourlyTripFieldsProps {
  date: string;
  startTime: string;
  hours: number;
  durationOptions: number[];
  twelveHour: boolean;
  availability?: AvailabilityData | null;
  /** Mobile renders the calendar inline (no nested sheet) and compacts rows. */
  mobile?: boolean;
  /** Human end label, e.g. "16:00" or "02:00 (next day)". */
  endLabel?: string | null;
  onDateChange: (date: string) => void;
  onStartChange: (time: string) => void;
  onHoursChange: (hours: number) => void;
}

/** Date + Start time + Duration for chauffeur bookings. Events use EventTripFields. */
export function HourlyTripFields({
  date, startTime, hours, durationOptions, twelveHour, availability, mobile = false, endLabel,
  onDateChange, onStartChange, onHoursChange,
}: HourlyTripFieldsProps) {
  const { t, i18n } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const startOptions = quarterHourStartOptions(twelveHour, startTime);
  const labelCls = "text-[10px] font-bold uppercase tracking-wider text-muted-foreground";
  const controlCls = cn(
    "relative flex min-w-0 w-full items-center gap-2 rounded-md border border-border bg-background text-left transition-colors hover:bg-muted/50",
    mobile ? "min-h-11 px-2.5" : "min-h-10 px-2.5",
  );
  const selectCls = "absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0";
  const durationLabel = (h: number) => h === FULL_DAY_HOURS
    ? t("booking.full_day_24h", "Full day (24h)")
    : t("booking.hours_count", { count: h, defaultValue: h === 1 ? "{{count}} hour" : "{{count}} hours" });
  const selectedStart = startOptions.find(o => o.value === startTime)?.label ?? startTime;

  const pick = (d: string) => {
    onDateChange(d);
    setCalendarOpen(false);
  };

  const dateButton = (
    <button
      type="button"
      onClick={() => setCalendarOpen(o => !o)}
      aria-expanded={calendarOpen}
      className={cn(controlCls, calendarOpen && "border-foreground ring-1 ring-foreground")}
      data-testid="booking-hourly-date"
    >
      <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className={cn("truncate text-sm", date ? "text-foreground" : "text-muted-foreground")}>
        {date ? new Intl.DateTimeFormat(i18n?.resolvedLanguage || i18n?.language || "en", {
          weekday: "short", day: "numeric", month: "short",
          year: mobile ? undefined : "numeric", timeZone: "UTC",
        }).format(new Date(`${date}T12:00:00Z`)) : t("booking.select_date", "Select date")}
      </span>
    </button>
  );

  return (
    <div className="space-y-2" data-testid="booking-hourly-fields">
      <Popover open={!mobile && calendarOpen} onOpenChange={setCalendarOpen}>
        <div className={cn("grid gap-2 [&>*]:min-w-0", mobile ? "grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_minmax(0,1fr)]" : "grid-cols-1 sm:grid-cols-[1.3fr_1fr]")}>
          <div className={cn(!mobile && "sm:col-span-2")}>
            <p className={labelCls}>{t("booking.date", "Date")}</p>
            <div className="mt-1">
              {mobile ? dateButton : <PopoverAnchor asChild>{dateButton}</PopoverAnchor>}
            </div>
          </div>
          <div>
            <p className={labelCls}>{t("booking.start_time", "Start time")}</p>
            <div className={cn(controlCls, "mt-1")}>
              {!mobile && <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">{selectedStart}</span>
              <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <select
                aria-label={t("booking.start_time", "Start time")}
                data-testid="select-hourly-start"
                value={startTime}
                onChange={e => onStartChange(e.target.value)}
                className={selectCls}
              >
                {startOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <p className={labelCls}>{t("booking.duration", "Duration")}</p>
            <div className={cn(controlCls, "mt-1")}>
              {!mobile && <Timer className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">{mobile ? `${hours} h` : durationLabel(hours)}</span>
              <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <select
                aria-label={t("booking.duration", "Duration")}
                data-testid="select-hourly-duration"
                value={hours}
                onChange={e => onHoursChange(Number(e.target.value))}
                className={selectCls}
              >
                {durationOptions.map(h => <option key={h} value={h}>{durationLabel(h)}</option>)}
              </select>
            </div>
          </div>
        </div>
        {!mobile && (
          <PopoverContent side="left" align="start" sideOffset={16} collisionPadding={16}
            className="w-[340px] rounded-2xl border border-border/70 bg-background p-4 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)]"
            data-testid="booking-hourly-date-popup">
            <SingleDateCalendar value={date} onSelect={pick} availability={availability} />
          </PopoverContent>
        )}
      </Popover>
      {mobile && calendarOpen && (
        <div className="rounded-lg border border-border p-2" data-testid="booking-hourly-inline-calendar">
          <SingleDateCalendar value={date} onSelect={pick} availability={availability} />
        </div>
      )}
      {date && endLabel ? (
        <p className="px-0.5 text-xs text-muted-foreground" data-testid="text-hourly-end">
          {t("booking.ends_at", "Ends {{end}}", { end: endLabel })}
        </p>
      ) : null}
    </div>
  );
}

export default HourlyTripFields;
